import { listPersonalSecurityEventsByCursor } from '../../../services/personalSecurityEvents';
import { supabase } from '../../../supabase';

/**
 * RD-SEC-015 flow D — event types whose LATEST occurrence means "this
 * person's authenticator entry was just removed out from under them" (an
 * admin/platform reset, a recovery-code redeem, or their own disable). When
 * the intro step opens right after one of these, it shows the "delete the
 * old entry first" warning note instead of the plain welcome copy — the old
 * "CrewVex" entry in their app is now dead and will always fail.
 *
 * Tenant + employee scopes share `mfa_disabled` (self-disable vs an admin
 * disabling someone else both land here); platform mirrors with its own
 * prefixed event types. `mfa_factor_removed` / `platform_mfa_factor_removed`
 * are emitted by the RD-SEC-015 lifecycle sweeper (Lane A) — this list
 * already includes them so the note activates the moment that migration + the
 * personalSecurityEvents.js allowlist (Lane C) ship, with no further change
 * here.
 */
const REMOVAL_EVENT_TYPES = new Set([
  'mfa_disabled',
  'mfa_factor_removed',
  'mfa_recovery_used',
  'mfa_email_recovery_used',
  'platform_mfa_disabled',
  'platform_mfa_factor_removed',
  'platform_mfa_recovery_code_used',
  'platform_mfa_email_recovery_used',
  'platform_mfa_reset',
  'platform_tenant_admin_mfa_reset',
]);

const TENANT_MFA_LIFECYCLE = [
  'mfa_disabled', 'mfa_factor_removed', 'mfa_recovery_used', 'mfa_email_recovery_used',
  'mfa_enrolled', 'mfa_factor_added',
];
// Platform: the lifecycle set lives INSIDE the `get_my_platform_mfa_reset_note`
// RPC (KAN-674) — platform_security_events SELECT requires aal2, but this note
// is read at aal1 by the forced enrollment gate. `platform_tenant_admin_mfa_reset`
// is deliberately not in that set (not a personal event type).

/**
 * Classify the latest MFA-lifecycle event.
 *  - `'selfOff'` — the user turned it off themselves (tenant: `mfa_disabled`
 *    on the `auth` entity with actor = self, which is what the lifecycle
 *    sweeper writes; platform: `platform_mfa_disabled` with actor = self).
 *  - `'reset'` — any other removal (admin/platform reset, recovery-code or
 *    email recovery, factor removed).
 *  - `null` — no event, or the latest one is not a removal (e.g. an
 *    enroll/add: the old entry was already replaced — nothing to warn about).
 */
export function classifyMfaResetEvent(latest, userId) {
  if (!latest || !REMOVAL_EVENT_TYPES.has(latest.event_type)) return null;
  if (latest.event_type === 'mfa_disabled' && latest.entity_type === 'auth' && latest.actor_id === userId) {
    return 'selfOff';
  }
  if (latest.event_type === 'platform_mfa_disabled' && latest.actor_user_id === userId) return 'selfOff';
  return 'reset';
}

/**
 * Uncached read — the reset-note query (formerly the useMfaResetNote hook). Reads
 * ONLY the latest MFA-lifecycle event (filtered server-side by event_type),
 * so an unrelated later event such as `password_changed` can't hide the
 * note. A read error (or a thrown/rejected request — e.g. a test's unmocked
 * `supabase.from`) resolves to `null`, never rejects — "no note" is the safe
 * default.
 */
export async function fetchMfaResetNoteRaw({ userId, tenantId }) {
  if (!userId) return null;
  try {
    if (!tenantId) {
      const { data, error } = await Promise.resolve(supabase.rpc('get_my_platform_mfa_reset_note'));
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return classifyMfaResetEvent(
        { event_type: row.event_type, actor_user_id: row.actor_is_self ? userId : null },
        userId,
      );
    }
    const { data, error } = await Promise.resolve(
      listPersonalSecurityEventsByCursor({ userId, eventType: TENANT_MFA_LIFECYCLE, limit: 1 }),
    );
    if (error) return null;
    return classifyMfaResetEvent(data?.[0], userId);
  } catch (_err) {
    return null;
  }
}

/**
 * RD-SEC-015 (2026-09-25 "modal grows after opening" fix) — the intro step's
 * reset-note query used to start on modal-open (the old useMfaResetNote hook's own
 * effect), so the note routinely arrived a beat AFTER the intro had already
 * painted at its note-less height, growing the modal a second time right as
 * the founder started reading it. This module makes the note knowable
 * BEFORE the modal opens: `TwoFactorSettingsRow` prefetches it the moment
 * the Account row mounts, so by the time the user taps to open the stepper
 * the value is already cached and `peekMfaResetNote` returns it
 * synchronously on first render — no growth at all. The forced gates (which
 * have no earlier row-mount to prefetch from) call `prefetchMfaResetNote`
 * themselves from `useMfaStepperUi`, bounded by `MFA_RESET_NOTE_PREFETCH_CAP_MS`
 * so a slow read can never hold the intro open indefinitely (see that hook
 * for the "show intro without the note past the cap, then ease it in late
 * via AutoHeight" fallback).
 *
 * Cached PER USER for the session (module-level — cleared on sign-out via
 * `clearMfaResetNoteCache`, wired from `clearPerUserDeviceState`, so a
 * shared-device user switch can never read a stale prior user's note).
 *
 * Forced gates (RD-SEC-021): `ProtectedRoute` / `PlatformRoute` prefetch
 * under the splash BEFORE flipping `mfaEnrollmentRequired`, so the intro
 * paints at final height on first frame and the logo glide never re-aims
 * through a mid-open grow. `useMfaStepperUi` still prefetches as a fallback
 * (mid-session opens with no splash cover).
 */
export const MFA_RESET_NOTE_PREFETCH_CAP_MS = 800;

/** userId -> { promise: Promise<variant>, settled: boolean, value: variant } */
const cache = new Map();

/**
 * Starts (or reuses an in-flight/already-resolved) fetch for `userId` and
 * returns its promise. Safe to call repeatedly (row mount, gate mount) —
 * only the first caller for a given userId actually issues the query.
 */
export function prefetchMfaResetNote({ userId, tenantId }) {
  if (!userId) return Promise.resolve(null);
  const existing = cache.get(userId);
  if (existing) return existing.promise;

  const entry = { settled: false, value: null, promise: null };
  entry.promise = fetchMfaResetNoteRaw({ userId, tenantId }).then((variant) => {
    entry.settled = true;
    entry.value = variant;
    return variant;
  });
  cache.set(userId, entry);
  return entry.promise;
}

/** Synchronous read of a settled cache entry — `undefined` when unknown
 *  (never prefetched, or still in flight), so a caller can tell "no note"
 *  (`null`) apart from "haven't checked yet". */
export function peekMfaResetNote(userId) {
  if (!userId) return undefined;
  const entry = cache.get(userId);
  return entry?.settled ? entry.value : undefined;
}

/**
 * Drop `userId`'s cached note so the next `prefetchMfaResetNote` re-queries
 * — call this right after any write that can change the latest MFA-lifecycle
 * event (enroll, turn off, change phone), same call sites that already
 * `refreshAll()` the factor list.
 */
export function invalidateMfaResetNote(userId) {
  if (userId) cache.delete(userId);
}

/** Sign-out hook (`clearPerUserDeviceState`) — the whole cache is per-device
 *  session state, never allowed to leak to the next user on a shared browser. */
export function clearMfaResetNoteCache() {
  cache.clear();
}
